import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TemplateManager = ({ language }) => {
  const [templates, setTemplates] = useState([
    {
      id: 't1',
      name: 'Rappel niveau 1',
      subject: 'Rappel de paiement',
      body: 'Bonjour {client_name},\n\nNous vous rappelons que le paiement de {amount} pour la facture {invoice_number} est dû depuis le {due_date}.\n\nMerci de régulariser votre situation.',
      lang: 'FR',
      channel: 'email',
      isActive: true
    },
    {
      id: 't2',
      name: 'Rappel niveau 2',
      subject: 'Paiement en retard - Action requise',
      body: 'Bonjour {client_name},\n\nVotre paiement de {amount} pour la facture {invoice_number} est en retard de {days_overdue} jours.\n\nMerci de procéder au paiement dans les plus brefs délais.',
      lang: 'FR',
      channel: 'email',
      isActive: true
    },
    {
      id: 't3',
      name: 'Rappel niveau 3 - Final',
      subject: 'Dernier rappel avant action légale',
      body: 'Bonjour {client_name},\n\nDernier rappel concernant le paiement de {amount} pour la facture {invoice_number}, en retard de {days_overdue} jours.\n\nSans régularisation sous 7 jours, nous serons contraints d\'engager des poursuites.',
      lang: 'FR',
      channel: 'email',
      isActive: true
    }
  ]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const languageOptions = [
    { value: 'FR', label: 'Français' },
    { value: 'EN', label: 'English' }
  ];

  const channelOptions = [
    { value: 'email', label: 'Email' },
    { value: 'dashboard', label: 'Tableau de bord' }
  ];

  const mergeFields = [
    { field: '{client_name}', description: 'Nom du client' },
    { field: '{amount}', description: 'Montant' },
    { field: '{invoice_number}', description: 'Numéro de facture' },
    { field: '{due_date}', description: 'Date d\'échéance' },
    { field: '{days_overdue}', description: 'Jours de retard' },
    { field: '{payment_link}', description: 'Lien de paiement' }
  ];

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingTemplate({
      id: Date.now()?.toString(),
      name: '',
      subject: '',
      body: '',
      lang: 'FR',
      channel: 'email',
      isActive: true
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if (editingTemplate?.id && templates?.find(t => t?.id === editingTemplate?.id)) {
      setTemplates(templates?.map(t => t?.id === editingTemplate?.id ? editingTemplate : t));
    } else {
      setTemplates([...templates, editingTemplate]);
    }
    setShowModal(false);
    setEditingTemplate(null);
  };

  const handleToggleActive = (templateId) => {
    setTemplates(templates?.map(t => 
      t?.id === templateId ? { ...t, isActive: !t?.isActive } : t
    ));
  };

  const handleDelete = (templateId) => {
    if (window.confirm('Supprimer ce template ?')) {
      setTemplates(templates?.filter(t => t?.id !== templateId));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Templates de messages
          </h2>
          <p className="text-sm text-muted-foreground">
            Gérez vos templates de rappels avec champs de fusion
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Icon name="Plus" size={18} className="mr-2" />
          Nouveau template
        </Button>
      </div>

      {/* Merge Fields Reference */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Info" size={18} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">
            Champs de fusion disponibles
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {mergeFields?.map((field) => (
            <div key={field?.field} className="flex items-center gap-2">
              <code className="text-xs bg-primary/10 px-2 py-1 rounded text-primary font-mono">{field?.field}</code>
              <span className="text-xs text-muted-foreground">{field?.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Templates List */}
      <div className="space-y-4">
        {templates?.map((template) => (
          <div key={template?.id} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">{template?.name}</h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground font-medium">
                    {template?.lang}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                    {template?.channel}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={template?.isActive}
                      onChange={() => handleToggleActive(template?.id)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
                <p className="text-sm font-medium text-foreground mb-2">{template?.subject}</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{template?.body}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => handleEdit(template)}>
                  <Icon name="Edit" size={18} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(template?.id)}>
                  <Icon name="Trash2" size={18} className="text-error" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {showModal && editingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="text-xl font-semibold text-foreground">
                {editingTemplate?.id && templates?.find(t => t?.id === editingTemplate?.id)
                  ? 'Modifier le template'
                  : 'Nouveau template'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-muted rounded-lg">
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <Input
                label="Nom du template"
                value={editingTemplate?.name}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e?.target?.value })}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Langue"
                  options={languageOptions}
                  value={editingTemplate?.lang}
                  onChange={(value) => setEditingTemplate({ ...editingTemplate, lang: value })}
                />
                <Select
                  label="Canal"
                  options={channelOptions}
                  value={editingTemplate?.channel}
                  onChange={(value) => setEditingTemplate({ ...editingTemplate, channel: value })}
                />
              </div>
              <Input
                label="Sujet"
                value={editingTemplate?.subject}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e?.target?.value })}
                required
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Corps du message
                </label>
                <textarea
                  value={editingTemplate?.body}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e?.target?.value })}
                  rows={8}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSave}>
                <Icon name="Save" size={18} className="mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;